const CollectionUtil = (function (){

    const handleFilterButtonClick = function(){
        const filterDrawerActionBtn = document.querySelectorAll('[filter-drawer-open]');
        [...filterDrawerActionBtn].forEach( btn => {
            btn.addEventListener('click', () => {
                HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, document, {
                    drawerType: 'filter',
                });
            })
        })
    }

    const initEvents = function(){
        handleFilterButtonClick();
    }

    return{
        initEvents,
    }

})();

document.addEventListener('DOMContentLoaded', () => {
    CollectionUtil.initEvents();
})

class CollectionSearch extends HTMLElement {
    static query = '';
    static cache = new Map();
    static pending = new Set();

    connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.clearButton = this.querySelector('.collectionList__searchClear');
        if (!this.input || !this.clearButton) return;

        const params = new URLSearchParams(window.location.search);
        CollectionSearch.query = params.get('collection_search') || '';
        this.searchPage = Math.max(1, Number.parseInt(params.get('search_page'), 10) || 1);
        this.input.value = CollectionSearch.query;
        this.originalGrid = document.getElementById(this.input.getAttribute('aria-controls'))?.innerHTML || '';
        this.originalPagination = this.getPaginationContainer()?.innerHTML || '';
        this.handleInput = this.debounce(() => {
            CollectionSearch.query = this.input.value;
            this.searchPage = 1;
            this.runSearch();
        }, 300);
        this.input.addEventListener('input', this.handleInput);
        this.clearButton.addEventListener('click', () => {
            this.input.value = '';
            CollectionSearch.query = '';
            this.searchPage = 1;
            this.restoreCollection();
            this.input.focus();
        });
        if (CollectionSearch.query.trim()) this.runSearch();
    }

    debounce(callback, delay) {
        let timeout;
        return (...args) => {
            window.clearTimeout(timeout);
            timeout = window.setTimeout(() => callback(...args), delay);
        };
    }

    getPaginationContainer() {
        const grid = document.getElementById(this.input.getAttribute('aria-controls'));
        return grid?.closest('[grid-layout-container]')?.parentElement?.querySelector('.pagination__contiainer');
    }

    getCollectionRequestParams() {
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.delete('section_id');
        params.delete('collection_search');
        params.delete('search_page');
        return params;
    }

    async fetchAllProducts() {
        const params = this.getCollectionRequestParams();
        const cacheKey = `${window.location.pathname}?${params.toString()}`;
        this.isInitialFetch = !CollectionSearch.cache.has(cacheKey) || CollectionSearch.pending.has(cacheKey);
        if (CollectionSearch.cache.has(cacheKey)) return CollectionSearch.cache.get(cacheKey);

        const pageCount = Math.max(1, Number.parseInt(this.dataset.pageCount, 10) || 1);
        const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);
        const request = (async () => {
            const products = [];
            const workers = Array.from({ length: Math.min(4, pageCount) }, async () => {
                while (pageNumbers.length) {
                    const page = pageNumbers.shift();
                    const pageParams = new URLSearchParams(params);
                    pageParams.set('section_id', this.dataset.sectionId);
                    pageParams.set('page', page);
                    const response = await fetch(`${window.location.pathname}?${pageParams.toString()}`);
                    if (!response.ok) throw new Error(`Collection page ${page} could not be loaded`);
                    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
                    documentFragment.querySelectorAll('[data-collection-product]').forEach((product) => {
                        products.push({ page, html: product.outerHTML, searchText: product.dataset.searchText || '' });
                    });
                }
            });
            await Promise.all(workers);
            return products
                .sort((first, second) => first.page - second.page)
                .map(({ html, searchText }) => ({ html, searchText }));
        })();

        CollectionSearch.cache.set(cacheKey, request);
        CollectionSearch.pending.add(cacheKey);
        request
            .catch(() => CollectionSearch.cache.delete(cacheKey))
            .finally(() => CollectionSearch.pending.delete(cacheKey));
        return request;
    }

    async runSearch() {
        const grid = document.getElementById(this.input.getAttribute('aria-controls'));
        if (!grid) return;

        const query = this.input.value.trim().toLocaleLowerCase();
        this.toggleAttribute('data-has-value', query.length > 0);
        if (!query) {
            this.restoreCollection();
            return;
        }

        grid.setAttribute('aria-busy', 'true');
        try {
            const productsRequest = this.fetchAllProducts();
            this.toggleAttribute('data-loading', this.isInitialFetch);
            this.input.setAttribute('aria-busy', this.isInitialFetch ? 'true' : 'false');
            const products = await productsRequest;
            if (query !== this.input.value.trim().toLocaleLowerCase()) return;
            this.matches = products.filter((product) => product.searchText.toLocaleLowerCase().includes(query));
            const pageTotal = Math.max(1, Math.ceil(this.matches.length / this.productsPerPage));
            this.searchPage = Math.min(this.searchPage, pageTotal);
            this.renderResults();
        } catch (error) {
            console.error('Collection search failed', error);
        } finally {
            grid.removeAttribute('aria-busy');
            this.removeAttribute('data-loading');
            this.input.removeAttribute('aria-busy');
        }
    }

    get productsPerPage() {
        return Math.max(1, Number.parseInt(this.dataset.productsPerPage, 10) || 16);
    }

    renderResults() {
        const grid = document.getElementById(this.input.getAttribute('aria-controls'));
        if (!grid) return;
        const start = (this.searchPage - 1) * this.productsPerPage;
        grid.innerHTML = this.matches.slice(start, start + this.productsPerPage).map((product) => product.html).join('');
        this.renderEmptyState(grid);
        this.renderPagination();
        this.updateCount(this.matches.length);
        this.updateUrl();
    }

    renderEmptyState(grid) {
        let emptyMessage = grid.parentElement.querySelector('.collectionList__searchEmpty');
        if (!emptyMessage) {
            emptyMessage = document.createElement('div');
            emptyMessage.className = 'collectionList__searchEmpty';
            emptyMessage.innerHTML = '<p class="collectionList__searchEmptyHeading"></p><p class="collectionList__searchEmptyText"></p>';
            grid.insertAdjacentElement('afterend', emptyMessage);
        }
        emptyMessage.querySelector('.collectionList__searchEmptyHeading').textContent = `${this.dataset.emptyHeading} “${this.input.value.trim()}”`;
        emptyMessage.querySelector('.collectionList__searchEmptyText').textContent = this.dataset.emptyMessage;
        emptyMessage.hidden = this.matches.length > 0;
    }

    renderPagination() {
        const container = this.getPaginationContainer();
        if (!container) return;
        const pageTotal = Math.ceil(this.matches.length / this.productsPerPage);
        if (pageTotal <= 1) {
            container.innerHTML = '';
            return;
        }

        const pages = this.paginationParts(pageTotal);
        const previous = this.searchPage > 1 ? `<li><a href="#" aria-label="Previous page" data-search-page="${this.searchPage - 1}">‹</a></li>` : '';
        const next = this.searchPage < pageTotal ? `<li><a href="#" aria-label="Next page" data-search-page="${this.searchPage + 1}">›</a></li>` : '';
        container.innerHTML = `<nav aria-label="pagination" role="navigation"><ol class="pagination">${previous}${pages.map((page) => {
            if (page === '…') return '<li><span>…</span></li>';
            const active = page === this.searchPage;
            return `<li${active ? ' aria-current="page" class="active"' : ''}>${active ? `<span><span class="visuallyhidden">page</span><span>${page}</span></span>` : `<a href="#" data-search-page="${page}"><span class="visuallyhidden">page</span><span>${page}</span></a>`}</li>`;
        }).join('')}${next}</ol></nav>`;
        container.querySelectorAll('[data-search-page]').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                this.searchPage = Number.parseInt(button.dataset.searchPage, 10);
                this.renderResults();
                document.getElementById('collection-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    }

    paginationParts(pageTotal) {
        const pages = new Set([1, pageTotal]);
        for (let page = Math.max(1, this.searchPage - 2); page <= Math.min(pageTotal, this.searchPage + 2); page += 1) pages.add(page);
        const sorted = [...pages].sort((first, second) => first - second);
        return sorted.flatMap((page, index) => index && page - sorted[index - 1] > 1 ? ['…', page] : [page]);
    }

    updateCount(count) {
        document.querySelectorAll('#collection-grid .collection__product__count').forEach((element) => {
            if (this.input.value.trim()) {
                element.replaceChildren();
                const total = document.createElement('strong');
                total.textContent = `${count} ${this.dataset.resultUnit}`;
                element.append(total, ` ${this.dataset.resultMatches} “${this.input.value.trim()}”`);
            } else {
                element.replaceChildren();
                const total = document.createElement('strong');
                total.textContent = this.dataset.countTemplate.replace('__COUNT__', count);
                element.append(total);
            }
        });
    }

    updateUrl() {
        const params = new URLSearchParams(window.location.search);
        params.set('collection_search', this.input.value.trim());
        if (this.searchPage > 1) params.set('search_page', this.searchPage);
        else params.delete('search_page');
        history.replaceState({ searchParams: params.toString() }, '', `${window.location.pathname}?${params.toString()}`);
    }

    restoreCollection() {
        const grid = document.getElementById(this.input.getAttribute('aria-controls'));
        if (grid) grid.innerHTML = this.originalGrid;
        const emptyMessage = grid?.parentElement.querySelector('.collectionList__searchEmpty');
        if (emptyMessage) emptyMessage.remove();
        const pagination = this.getPaginationContainer();
        if (pagination) pagination.innerHTML = this.originalPagination;
        this.updateCount(Number.parseInt(this.dataset.totalProducts, 10) || 0);
        this.toggleAttribute('data-has-value', false);
        const params = new URLSearchParams(window.location.search);
        params.delete('collection_search');
        params.delete('search_page');
        history.replaceState({ searchParams: params.toString() }, '', `${window.location.pathname}${params.size ? `?${params.toString()}` : ''}`);
    }
}

customElements.get('collection-search') ||
    customElements.define('collection-search', CollectionSearch);
